import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useLocation } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import { driver, type Driver } from "driver.js";
import "driver.js/dist/driver.css";
import {
  useUpdateTourState,
  getGetMeQueryKey,
} from "@workspace/api-client-react";
import { TOUR_STEPS, type TourStep } from "./tour-steps";

const LS_PREFIX = "spn.tourState:";

type LocalTourState = {
  completedAt: string | null;
  dismissedAt: string | null;
};

function lsKey(userId: string | null | undefined): string | null {
  if (!userId) return null;
  return `${LS_PREFIX}${userId}`;
}

function readLocal(userId: string | null | undefined): LocalTourState {
  const key = lsKey(userId);
  if (!key) return { completedAt: null, dismissedAt: null };
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return { completedAt: null, dismissedAt: null };
    const parsed = JSON.parse(raw) as Partial<LocalTourState>;
    return {
      completedAt: parsed.completedAt ?? null,
      dismissedAt: parsed.dismissedAt ?? null,
    };
  } catch {
    return { completedAt: null, dismissedAt: null };
  }
}

function writeLocal(userId: string | null | undefined, state: LocalTourState): void {
  const key = lsKey(userId);
  if (!key) return;
  try {
    sessionStorage.setItem(key, JSON.stringify(state));
  } catch {
    /* ignore quota */
  }
}

type TourCtx = {
  startTour: (fromIndex?: number) => void;
  stopTour: () => void;
  dismissTour: () => void;
  completeTour: () => void;
  showFirstLoginPrompt: boolean;
  setShowFirstLoginPrompt: (v: boolean) => void;
  showPageHelp: boolean;
  openPageHelp: () => void;
  setShowPageHelp: (v: boolean) => void;
  isActive: boolean;
  currentStepIndex: number | null;
};

const TourContext = createContext<TourCtx | null>(null);

export function useTour(): TourCtx {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error("useTour must be used within TourProvider");
  return ctx;
}

// Provider-tolerant variant: returns null when no TourProvider is mounted.
// Use this from components (callouts, tooltips) that may render inside
// isolated test harnesses or other shells that don't include the tour.
export function useOptionalTour(): TourCtx | null {
  return useContext(TourContext);
}

interface TourProviderProps {
  children: ReactNode;
  initialFirstLogin: boolean;
  userId?: string | null;
}

export function TourProvider({ children, initialFirstLogin, userId }: TourProviderProps) {
  const [location, navigate] = useLocation();
  const qc = useQueryClient();
  const driverRef = useRef<Driver | null>(null);
  const [currentStepIndex, setCurrentStepIndex] = useState<number | null>(null);

  // Server state (initialFirstLogin) is the source of truth. The per-user
  // sessionStorage fallback only suppresses the prompt as a transient guard
  // while the dismiss/complete mutation is in flight, scoped strictly to the
  // current user so a previous user on the same browser can never hide
  // onboarding for a newly authenticated one.
  const local = readLocal(userId);
  const seenLocallyForThisUser = !!local.completedAt || !!local.dismissedAt;
  const [showFirstLoginPrompt, setShowFirstLoginPrompt] = useState(
    initialFirstLogin && !seenLocallyForThisUser,
  );

  // Recompute when the authenticated user changes (e.g. login as user B after
  // user A) or when /auth/me resolves with new tour state.
  useEffect(() => {
    const fresh = readLocal(userId);
    const seen = !!fresh.completedAt || !!fresh.dismissedAt;
    setShowFirstLoginPrompt(initialFirstLogin && !seen);
  }, [userId, initialFirstLogin]);

  const [showPageHelp, setShowPageHelp] = useState(false);

  const updateMut = useUpdateTourState({
    mutation: {
      onSuccess: () => qc.invalidateQueries({ queryKey: getGetMeQueryKey() }),
    },
  });

  const persistComplete = useCallback(() => {
    writeLocal(userId, { completedAt: new Date().toISOString(), dismissedAt: null });
    updateMut.mutate({ data: { action: "complete" } });
  }, [updateMut, userId]);

  const persistDismiss = useCallback(() => {
    const prior = readLocal(userId);
    writeLocal(userId, {
      completedAt: prior.completedAt,
      dismissedAt: new Date().toISOString(),
    });
    updateMut.mutate({ data: { action: "dismiss" } });
  }, [updateMut, userId]);

  // Flag set while we are programmatically tearing down driver.js so that the
  // ensuing onDestroyStarted callback doesn't get treated as a user-initiated
  // close (which would mis-persist dismiss/complete during Next/Back).
  const internalTeardownRef = useRef(false);

  const tearDownDriver = useCallback(() => {
    if (driverRef.current) {
      const d = driverRef.current;
      driverRef.current = null;
      internalTeardownRef.current = true;
      try {
        d.destroy();
      } catch {
        /* ignore */
      } finally {
        internalTeardownRef.current = false;
      }
    }
  }, []);

  const stopTour = useCallback(() => {
    tearDownDriver();
    setCurrentStepIndex(null);
  }, [tearDownDriver]);

  const completeTour = useCallback(() => {
    stopTour();
    persistComplete();
  }, [stopTour, persistComplete]);

  const dismissTour = useCallback(() => {
    setShowFirstLoginPrompt(false);
    stopTour();
    persistDismiss();
  }, [stopTour, persistDismiss]);

  const startTour = useCallback(
    (fromIndex = 0) => {
      setShowFirstLoginPrompt(false);
      const step = TOUR_STEPS[fromIndex];
      if (!step) return;
      setCurrentStepIndex(fromIndex);
      if (location !== step.route) navigate(step.route);
    },
    [location, navigate],
  );

  // Render the popover for the active step whenever the route matches the
  // step's route. This decouples navigation from popover rendering so that
  // Next/Back deterministically advances after the new page mounts.
  useEffect(() => {
    if (currentStepIndex === null) return;
    const step = TOUR_STEPS[currentStepIndex];
    if (!step || location !== step.route) return;

    let cancelled = false;
    // Give the new page a frame to mount its data-tour anchor.
    const timer = window.setTimeout(() => {
      if (cancelled) return;
      tearDownDriver();
      const target = document.querySelector(step.selector);
      const isLast = currentStepIndex + 1 >= TOUR_STEPS.length;
      const d = driver({
        showProgress: true,
        showButtons: ["next", "previous", "close"],
        progressText: `Step ${currentStepIndex + 1} of ${TOUR_STEPS.length}`,
        nextBtnText: isLast ? "Done" : "Next",
        prevBtnText: "Back",
        allowClose: true,
        onCloseClick: () => {
          if (internalTeardownRef.current) return;
          tearDownDriver();
          setCurrentStepIndex(null);
          // Persist terminal state for every user-initiated close path so the
          // first-login prompt never reappears. On the final step, treat the
          // close as completion; otherwise treat it as a dismiss.
          if (isLast) persistComplete();
          else persistDismiss();
        },
        onDestroyStarted: () => {
          // Skip when this destroy was initiated by us (Next/Back/stop) — it's
          // not a user-initiated close.
          if (internalTeardownRef.current) return;
          tearDownDriver();
          setCurrentStepIndex(null);
          if (isLast) persistComplete();
          else persistDismiss();
        },
        onNextClick: () => {
          tearDownDriver();
          if (isLast) {
            setCurrentStepIndex(null);
            persistComplete();
          } else {
            setCurrentStepIndex(currentStepIndex + 1);
          }
        },
        onPrevClick: () => {
          tearDownDriver();
          const prev = Math.max(0, currentStepIndex - 1);
          setCurrentStepIndex(prev);
        },
        steps: [
          {
            element: target ?? undefined,
            popover: {
              title: step.title,
              description: step.description,
              side: "bottom",
              align: "start",
            },
          },
        ],
      });
      driverRef.current = d;
      d.drive();
    }, 80);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [currentStepIndex, location, tearDownDriver, persistComplete, persistDismiss]);

  // When the active step's route is different from the current route, push
  // the route so the popover effect above can fire on the new page.
  useEffect(() => {
    if (currentStepIndex === null) return;
    const step = TOUR_STEPS[currentStepIndex];
    if (!step) return;
    if (location !== step.route) navigate(step.route);
  }, [currentStepIndex, location, navigate]);

  useEffect(() => {
    return () => {
      tearDownDriver();
    };
  }, [tearDownDriver]);

  const openPageHelp = useCallback(() => setShowPageHelp(true), []);

  const value = useMemo<TourCtx>(
    () => ({
      startTour,
      stopTour,
      dismissTour,
      completeTour,
      showFirstLoginPrompt,
      setShowFirstLoginPrompt,
      showPageHelp,
      openPageHelp,
      setShowPageHelp,
      isActive: currentStepIndex !== null,
      currentStepIndex,
    }),
    [
      startTour,
      stopTour,
      dismissTour,
      completeTour,
      showFirstLoginPrompt,
      showPageHelp,
      openPageHelp,
      currentStepIndex,
    ],
  );

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
}

export { TOUR_STEPS, type TourStep };
